/**
 * Reports API — итоги периода для вкладки «Головне» (#831), срез качества
 * дашборда (#1235), раздел «Голос клиента» (#848), отчёт по критериям (#866) и
 * отчёт по шагам скрипта (#879).
 */
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { REPORT_SUMMARY_FUNNEL_PARAM } from '@/lib/funnel-missed';
import {
  criteriaReportDetailResponseSchema,
  criteriaReportResponseSchema,
  dashboardFlowResponseSchema,
  dashboardQualityResponseSchema,
  dashboardQualitySectionsResponseSchema,
  dashboardTeamResponseSchema,
  dashboardSalesResponseSchema,
  dashboardVoiceResponseSchema,
  employeeIssuesResponseSchema,
  feedbackEffectResponseSchema,
  reportFlaggedResponseSchema,
  reportSummaryClientResponseSchema,
  scriptStepsReportDetailResponseSchema,
  serviceStatusResponseSchema,
  scriptStepsReportResponseSchema,
  voiceCustomerQuestionsOnlyResponseSchema,
  voiceFaqDraftResponseSchema,
  voiceOfCustomerResponseSchema,
  voiceThemesOnlyResponseSchema,
  voiceTrendResponseSchema,
  VOICE_FAQ_PROXY_DEADLINE_MS,
} from '@aica/api-types/schemas';
import type {
  AnalyzedCallCategory,
  CriteriaReportDetailResponse,
  CriteriaReportResponse,
  DashboardFlowChannel,
  DashboardFlowResponse,
  DashboardQualityResponse,
  DashboardQualitySectionsResponse,
  DashboardQualitySliceQuery,
  DashboardTeamResponse,
  DashboardSalesResponse,
  DashboardVoiceQuery,
  DashboardVoiceResponse,
  EmployeeIssuesResponse,
  FeedbackEffectResponse,
  ReportFlaggedCall,
  ReportFlaggedResponse,
  ServiceStatusQuery,
  ServiceStatusResponse,
  ReportSummaryClientResponse,
  ScriptStepsReportDetailResponse,
  ScriptStepsReportResponse,
  VoiceCustomerQuestionsOnlyResponse,
  VoiceFaqDraftResponse,
  VoiceOfCustomerResponse,
  VoiceThemeCuts,
  VoiceThemesOnlyResponse,
  VoiceTrendQuery,
  VoiceTrendResponse,
} from '@aica/api-types/schemas';

/**
 * Окно отчёта — одно на сводку и её продолжение (#1095): продолжение обязано
 * листать тот же список, и расхождение параметров разделило бы их.
 */
export type ReportWindowParams = {
  periodStart: string;
  periodEnd: string;
  /** Пусто — все доступные проекты (супервайзеру сервер подставит его собственные). */
  projectIds?: string[];
  includeDemo?: 'yes' | 'no';
};

/** Блок «После фидбека» (#2609): окна задаёт фидбек — периода нет. */
export type FeedbackEffectParams = Pick<ReportWindowParams, 'projectIds' | 'includeDemo'>;

/**
 * How a Voice of Customer read is asked (#2816): `lockedDemo` — it asks the
 * demo of a closed section, and bypasses the browser cache (`demoAskOptions`).
 */
export interface DemoAsk {
  lockedDemo?: boolean;
}

export class ReportsApi extends BaseApiClient {
  /**
   * Даты всегда явные: вкладка показывает тот же период, что и стрелки PeriodBar.
   * Узкий доступ (E1.5, #1336) получает только разделы своих агрегатов —
   * отсюда клиентская схема с необязательными разделами.
   */
  async getSummary(params: ReportWindowParams): Promise<ReportSummaryClientResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_SUMMARY}${this.buildQuery({ ...params, ...REPORT_SUMMARY_FUNNEL_PARAM })}`,
      reportSummaryClientResponseSchema
    );
  }

  /**
   * Просадки сотрудника в «Команде» (#767): топ-проблемы «Главного» за то же
   * окно, суженные до строки «Команды» с ключом `employee`.
   */
  async getEmployeeIssues(
    params: ReportWindowParams & { employee: string }
  ): Promise<EmployeeIssuesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_EMPLOYEE_ISSUES}${this.buildQuery(params)}`,
      employeeIssuesResponseSchema
    );
  }

  /**
   * «После фидбека» в «Команде» (#2609): балл строки «Команды» с ключом
   * `employee` по критериям до и после последнего фидбека руководителя.
   */
  async getEmployeeFeedbackEffect(
    params: FeedbackEffectParams & { employee: string }
  ): Promise<FeedbackEffectResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_EMPLOYEE_FEEDBACK_EFFECT}${this.buildQuery(params)}`,
      feedbackEffectResponseSchema
    );
  }

  /** «Потребують уваги» после последней показанной строки (#1095). */
  async getFlaggedCalls(
    params: ReportWindowParams,
    after: ReportFlaggedCall
  ): Promise<ReportFlaggedResponse> {
    const query = this.buildQuery({
      ...params,
      afterSeverity: after.severity,
      afterCreatedAt: after.createdAt,
      afterRecordId: after.recordId,
    });
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_FLAGGED}${query}`,
      reportFlaggedResponseSchema
    );
  }

  /**
   * Срез качества (#1235): полуоткрытое окно `[periodStart, periodEnd)`,
   * дни режутся по зоне `timeZone` (IANA), без неё — по `tzOffset`. 404 —
   * закрытое превью не включено для организации. `skills: '1'` — ответ несёт
   * навыки и цель компании: их рисует только вкладка «Качество» (#2355).
   */
  async getQualitySlice(
    params: ReportWindowParams & {
      timeZone?: string;
      tzOffset: number;
    } & Pick<DashboardQualitySliceQuery, 'skills'>
  ): Promise<DashboardQualityResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_QUALITY}${this.buildQuery(params)}`,
      dashboardQualityResponseSchema
    );
  }

  /**
   * Поток «Разговоров» единого дашборда (#1337): то же окно и та же область,
   * что у среза качества, плюс канал. 404 — превью единого дашборда не
   * включено для организации.
   */
  async getFlowSlice(
    params: ReportWindowParams & {
      timeZone?: string;
      tzOffset: number;
      channel?: DashboardFlowChannel;
    }
  ): Promise<DashboardFlowResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_FLOW}${this.buildQuery(params)}`,
      dashboardFlowResponseSchema
    );
  }

  /**
   * Блоки «Качества» единого дашборда, чьи данные лежат только в Postgres
   * (#1338): скрипт, возражения, соблюдение требований, проверка оценок,
   * критерии, кандидаты на проверку. То же окно, область и демо, что у среза
   * качества; каждый раздел отвечает сам (`ok` / `error` / `noAccess` /
   * `featureOff`). 404 — превью единого дашборда не включено для организации;
   * 403 — ни один из выбранных проектов не ваш.
   */
  async getQualitySections(
    params: ReportWindowParams & { timeZone?: string; tzOffset: number }
  ): Promise<DashboardQualitySectionsResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_QUALITY_SECTIONS}${this.buildQuery(params)}`,
      dashboardQualitySectionsResponseSchema
    );
  }

  /**
   * «Команда» единого дашборда (#1338): то же окно, та же область и демо, что
   * у среза качества; канала у команды нет. 404 — превью единого дашборда не
   * включено для организации; 403 — ни один из выбранных проектов не ваш.
   */
  async getTeamSlice(
    params: ReportWindowParams & { timeZone?: string; tzOffset: number }
  ): Promise<DashboardTeamResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_TEAM}${this.buildQuery(params)}`,
      dashboardTeamResponseSchema
    );
  }

  /**
   * «Продажи» единого дашборда (#1339). Pro и выше: без тарифа или флага
   * сервер отвечает 403 с `code` — вкладка показывает экран тарифа или
   * «функция выключена», а не ошибку.
   */
  async getSalesSlice(
    params: ReportWindowParams & { timeZone?: string; tzOffset: number }
  ): Promise<DashboardSalesResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_SALES}${this.buildQuery(params)}`,
      dashboardSalesResponseSchema
    );
  }

  /**
   * «Голос клиента» единого дашборда (#1340): сводки выпущенного раздела и
   * потока возражений, то же окно, область и демо, что у остальных срезов;
   * каждый раздел отвечает сам (`ok` / `error` / `noAccess` / `featureOff`).
   * Pro и выше: без тарифа или флага сервер отвечает 403 с `code` — вкладка
   * показывает экран тарифа или «функция выключена», а не ошибку.
   */
  async getVoiceSlice(
    params: ReportWindowParams & {
      timeZone?: string;
      tzOffset: number;
    } & Pick<DashboardVoiceQuery, 'sections'>
  ): Promise<DashboardVoiceResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_VOICE}${this.buildQuery(params)}`,
      dashboardVoiceResponseSchema
    );
  }

  /**
   * Настройки → «Работа сервиса» (#1341): очередь сейчас и обработка за 7 или
   * 30 дней; каждый блок отвечает сам (`ok` / `error`). Ошибка приходит
   * причиной, текст конвейера — только владельцу платформы (`detail`).
   */
  async getServiceStatus(
    period: ServiceStatusQuery['period']
  ): Promise<ServiceStatusResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.DASHBOARD_SERVICE}${this.buildQuery({ period })}`,
      serviceStatusResponseSchema
    );
  }

  /**
   * Раздел «Голос клиента» (#848). Pro и выше: без тарифа или флага сервер
   * отвечает 403 с `code` — экран показывает это словами, а не ошибкой.
   * `themeCode` — фильтр темы (#2098): ответ с эхом `themeFilter`; закрытый
   * флаг тем — 403 `themes_disabled`, тема не из набора — 400
   * `theme_filter_invalid` (так же у вопросов, потока возражений и черновика).
   */
  async getVoiceOfCustomer(
    params: ReportWindowParams & {
      callCategory?: AnalyzedCallCategory;
      themeCode?: string;
    },
    ask: DemoAsk = {}
  ): Promise<VoiceOfCustomerResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_VOICE}${this.buildQuery(params)}`,
      voiceOfCustomerResponseSchema,
      this.demoAskOptions(ask.lockedDemo)
    );
  }

  /**
   * Только вопросы клиентов и причины отказа (#843, #844) — вкладке «Продажи»
   * нужен один ключ раздела, не его сканы (`only=customerQuestions`). Те же
   * шлюзы и отказ 403 с `code`. `breakdowns: 'none'` — «Сайт и контент»: без
   * разбивок отказов и разговоров за ними — отказов вкладка не рисует.
   */
  async getVoiceCustomerQuestions(
    params: ReportWindowParams & {
      callCategory?: AnalyzedCallCategory;
      breakdowns?: 'none';
      themeCode?: string;
    },
    ask: DemoAsk = {}
  ): Promise<VoiceCustomerQuestionsOnlyResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_VOICE}${this.buildQuery({ ...params, only: 'customerQuestions' })}`,
      voiceCustomerQuestionsOnlyResponseSchema,
      this.demoAskOptions(ask.lockedDemo)
    );
  }

  /**
   * Только темы разговоров (#845 PR 6) — `only=themes`: те же окно, фильтр и
   * шлюзы, ответ не кешируется. `themeCuts` — какой срез читает экран:
   * `outcomes` — качество и отказы («Продажи»), `questions` — вопросы без
   * ответа («Сайт и контент»). Ключа `themes` в ответе нет, пока флаг
   * `enableConversationThemes` закрыт организации.
   */
  async getVoiceThemes(
    params: ReportWindowParams & {
      callCategory?: AnalyzedCallCategory;
      /** None — the rows alone, without the quality, declines and unanswered cuts. */
      themeCuts?: VoiceThemeCuts;
    },
    ask: DemoAsk = {}
  ): Promise<VoiceThemesOnlyResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_VOICE}${this.buildQuery({ ...params, only: 'themes' })}`,
      voiceThemesOnlyResponseSchema,
      this.demoAskOptions(ask.lockedDemo)
    );
  }

  /**
   * Ряд «Голоса клиента» по корзинам (#2394 §9 п. 5) — один сигнал, до
   * `VOICE_TREND_MAX_KEYS` строк. Ключи — повтором параметра, а не через
   * запятую (`buildQuery`): в имени конкурента запятая бывает. Те же шлюзы и
   * отказ 403 с `code`, что у раздела; сигналы шага — ещё `signal_disabled`.
   */
  async getVoiceSeries(
    params: Omit<VoiceTrendQuery, 'keys' | 'projectId'> & { keys: readonly string[] },
    ask: DemoAsk = {}
  ): Promise<VoiceTrendResponse> {
    const { keys, ...rest } = params;
    const query = new URLSearchParams(this.buildQuery(rest).slice(1));
    for (const key of keys) query.append('keys', key);
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_VOICE_SERIES}?${query.toString()}`,
      voiceTrendResponseSchema,
      this.demoAskOptions(ask.lockedDemo)
    );
  }

  /**
   * Черновик FAQ из вопросов клиентов (#843, §8 п. 6) — то же окно и фильтр,
   * что у раздела. Платный, поэтому БЕЗ повторов (`maxRetries: 0`): повтор —
   * это новое нажатие и слот суточного лимита. Отказы — статус с `code`
   * (403 / 429 / 503 / 500; кошелёк клиента кнопку не закрывает — платит
   * платформа); предел ожидания — на 10 с дольше, чем
   * edge-api ждёт workflows-api, чтобы прочитать его «слишком долго».
   */
  generateVoiceFaqDraft(
    params: ReportWindowParams & {
      callCategory?: AnalyzedCallCategory;
      themeCode?: string;
    }
  ): Promise<VoiceFaqDraftResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_VOICE}/faq-draft`,
      voiceFaqDraftResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(params),
        signal: AbortSignal.timeout(VOICE_FAQ_PROXY_DEADLINE_MS + 10_000),
      },
      0
    );
  }

  /**
   * Отчёт по критериям (#866) — сводка по всем критериям за окно. Дни и
   * недели режутся по зоне `timeZone`, без неё — по `tzOffset`.
   */
  async getCriteriaReport(
    params: ReportWindowParams & { timeZone?: string; tzOffset: number }
  ): Promise<CriteriaReportResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_CRITERIA}${this.buildQuery(params)}`,
      criteriaReportResponseSchema
    );
  }

  /** Один критерий: во времени, распределение, по менеджерам. */
  async getCriterionReport(
    criterionId: string,
    params: ReportWindowParams & { timeZone?: string; tzOffset: number }
  ): Promise<CriteriaReportDetailResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_CRITERIA}/${encodeURIComponent(criterionId)}${this.buildQuery(params)}`,
      criteriaReportDetailResponseSchema
    );
  }

  /** Отчёт по шагам скрипта (#879): скрипты → шаги, с покрытием «оценено N из M». */
  async getScriptStepsReport(
    params: ReportWindowParams
  ): Promise<ScriptStepsReportResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_SCRIPT_STEPS}${this.buildQuery(params)}`,
      scriptStepsReportResponseSchema
    );
  }

  /** Один шаг: менеджеры и критерии, из-за которых он не выполнен. */
  async getScriptStepReport(
    stepId: string,
    params: ReportWindowParams
  ): Promise<ScriptStepsReportDetailResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.REPORTS_SCRIPT_STEPS}/${encodeURIComponent(stepId)}${this.buildQuery(params)}`,
      scriptStepsReportDetailResponseSchema
    );
  }
}
